import { useEffect, useRef } from 'react'
import { getFreshAccessToken, getRefreshToken } from '../../../lib/api/api'

// The server delivers scan events only to the users of the scan's firm
// (firm_id) and to its uploader (user_id).
export type ScanReceivedEvent = {
  type: 'scan_received'
  scan_id: string
  file_name: string
  preview_url: string
  received_at: string
  firm_id?: string | null
  user_id?: string | null
}

export type ScanCompleteEvent = {
  type: 'scan_complete'
  scan_id: string
  doc_id: string | null
  title: string
  description: string
  doc_type: string
  error: string | null
}

export type ScanProgressEvent = {
  type: 'scan_progress'
  scan_id: string
  stage: 'text_extraction' | 'document_analysis' | 'chunking' | 'embedding' | 'vision_scan'
  stage_label: string
  total_pages: number | null
  chunks_total: number | null
  chunks_embedded: number | null
}

export type VisionPageEvent =
  | {
      type: 'vision_page'
      scan_id: string
      event: 'batch_start'
      total_pages: number
    }
  | {
      type: 'vision_page'
      scan_id: string
      event: 'start' | 'complete'
      page: number
    }

export type ScanEvent =
  | ScanReceivedEvent
  | ScanCompleteEvent
  | ScanProgressEvent
  | VisionPageEvent

export type ScanEventCallbacks = {
  onReceived?: (event: ScanReceivedEvent) => void
  onComplete?: (event: ScanCompleteEvent) => void
  onProgress?: (event: ScanProgressEvent) => void
  onVisionPage?: (event: VisionPageEvent) => void
}

const RECONNECT_BASE_DELAY_MS = 1000
const RECONNECT_MAX_DELAY_MS = 30000

/**
 * Subscribe to live scan-ingest events via SSE.
 *
 * Single EventSource against /api/v1/events/scans (access token in the query
 * param, because EventSource cannot send custom headers; the backend redacts
 * it from its logs). Dispatches to onReceived / onComplete based on the event
 * `type`.
 *
 * The browser auto-reconnects on transient drops, but always with the same
 * URL: once the access token has expired that reconnect gets a 401 and the
 * EventSource closes for good. So when it ends up CLOSED we reopen it
 * ourselves with a fresh token (shared refresh), with exponential backoff.
 */
export function useScanEvents(callbacks: ScanEventCallbacks): void {
  // Stable ref so reconnecting isn't required when the parent passes new closures.
  const callbacksRef = useRef(callbacks)
  useEffect(() => {
    callbacksRef.current = callbacks
  }, [callbacks])

  useEffect(() => {
    let disposed = false
    let es: EventSource | null = null
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    let attempt = 0

    const scheduleReconnect = () => {
      if (disposed || retryTimer) return
      const delay = Math.min(RECONNECT_MAX_DELAY_MS, RECONNECT_BASE_DELAY_MS * 2 ** attempt)
      attempt++
      retryTimer = setTimeout(() => {
        retryTimer = null
        connect()
      }, delay)
    }

    const connect = async () => {
      // Always an ACCESS token (refreshed if expiring): the endpoint rejects refresh tokens.
      const token = await getFreshAccessToken()
      if (disposed) return
      if (!token) {
        // Session expired: AuthContext will unmount us. Otherwise (backend
        // unreachable) keep trying with backoff.
        if (getRefreshToken()) scheduleReconnect()
        return
      }

      const source = new EventSource(`/api/v1/events/scans?token=${encodeURIComponent(token)}`)
      es = source

      source.onopen = () => {
        attempt = 0
      }

      source.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data) as ScanEvent
          if (!data || !data.type) return
          if (data.type === 'scan_received') {
            callbacksRef.current.onReceived?.(data)
          } else if (data.type === 'scan_complete') {
            callbacksRef.current.onComplete?.(data)
          } else if (data.type === 'scan_progress') {
            callbacksRef.current.onProgress?.(data)
          } else if (data.type === 'vision_page') {
            callbacksRef.current.onVisionPage?.(data)
          }
        } catch (err) {
          console.warn('[scan SSE] parse error', err)
        }
      }

      source.onerror = () => {
        // CONNECTING: the browser is retrying on its own. CLOSED: it gave up
        // (e.g. 401 with an expired token) — reopen with a fresh token.
        if (source.readyState === EventSource.CLOSED) {
          source.close()
          if (es === source) es = null
          scheduleReconnect()
        }
      }
    }

    connect()

    return () => {
      disposed = true
      if (retryTimer) clearTimeout(retryTimer)
      es?.close()
      es = null
    }
  }, [])
}
